import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Loader } from 'Components';
import { Box } from '@mui/material';
import { RequestsContext } from 'Context';
import { IFormMetaData, createRequest, getRequestFields } from 'API';
import { NoteTextField } from './NoteTextField';
import { SuccessDialog } from '../SuccessDialog';
import {
  useAppSelector,
  useDictionary,
  useDisabilityCertAccess,
  useDispatchApp,
  useImposter,
  useIsDesktop,
} from 'Hooks';
import { ActionButtons } from './ActionButtons';
import { dataMapper } from './Utils';
import { getPersonalDetails } from 'API/personalDetails';
import { setPersonalDetails } from 'Store/PersonalDetailsSlice';
import { PersonalAreaRecommendation } from './PersonalAreaRecommendation';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { REFUND_FALLBACK_REASON } from 'Enum/Refunds';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import {
  IDocumentTypeOption,
  IDocumentType,
  IPrefilledAttachment,
} from 'Hooks';
import {
  IRON_SWORDS_FORM_ID,
  PERSONAL_RECOMMENDATION_URLS,
  DISABLED_VETERAN_SUB_ID,
  MAX_DEPARTMENT_INITIATED_USER_NOTE_LENGTH,
} from 'Config';
import { ISubCategory } from 'API/UniversalRequest/universalRequest';
import { DocumentUploadManager } from './DocumentUploadManager';

export interface IFileUpload {
  subId: number;
  goBack: () => void;
  handleSuccess: () => void;
  resetRequest: () => void;
  titleLabel: string;
  onlineFormLink?: string;
  isDownload?: boolean;
  isOtp: boolean;
  subCategory?: ISubCategory;
}

export interface IError {
  isTypeError: boolean;
  isFileError: boolean;
  isServerError: boolean;
}

export const FileUpload = forwardRef<HTMLButtonElement, IFileUpload>(
  (props, ref) => {
    const {
      goBack,
      subId,
      handleSuccess,
      resetRequest,
      titleLabel,
      onlineFormLink,
      isDownload,
      isOtp,
      subCategory,
    } = props;

    const { requestsData, setRequestsData } = useContext(RequestsContext);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [uploadFilesList, setUploadFilesList] = useState<IFormMetaData>();
    const [note, setNote] = useState('');
    const [internetReqNum, setInternetReqNum] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isNoteError, setIsNoteError] = useState<boolean>(false);
    const [uploadedDocumentTypes, setUploadedDocumentTypes] = useState<
      IDocumentType[]
    >([]);

    const isDesktop = useIsDesktop();
    const { imposterId } = useImposter();
    const { personalDetails } = useAppSelector(
      ({ personalDetails }) => personalDetails,
    );
    const location = useLocation();
    const locationState = location.state as {
      customNoteTitle?: string;
      refundFailureReason?: REFUND_FALLBACK_REASON;
      prefilledInvoiceFile?: File;
    } | null;

    const availableDocumentTypes = useMemo((): IDocumentTypeOption[] => {
      if (!uploadFilesList?.attachments) return [];

      return uploadFilesList.attachments.map((attachment) => ({
        type: attachment.id,
        label: attachment.name,
      }));
    }, [uploadFilesList]);

    const getUploadFileList = useCallback(async () => {
      if (subId) {
        const getFields = await getRequestFields(subId);
        if (getFields) {
          setUploadFilesList(getFields);
        }
      }
    }, [subId]);

    const sanitizeNote = (noteText: string): string => {
      return noteText.replace(/\r?\n/g, ' ');
    };

    const syncToRequestsData = useCallback(() => {
      if (!uploadedDocumentTypes || uploadedDocumentTypes.length === 0) {
        setRequestsData((prevRequestData) => ({
          ...prevRequestData,
          fieldAttachmentsData: [],
          note: note,
          type: subId,
        }));
        return;
      }

      const fieldAttachmentsData = uploadedDocumentTypes.map((docType) => {
        const attachments: Record<string, File> = {};

        docType.files.forEach((uploadedFile: any) => {
          if (uploadedFile.file) {
            attachments[uploadedFile.name] = uploadedFile.file;
          }
        });

        return {
          fieldId: docType.type,
          attachments,
        };
      });

      setRequestsData((prevRequestData) => ({
        ...prevRequestData,
        fieldAttachmentsData,
        note: note,
        type: subId,
      }));
    }, [uploadedDocumentTypes, note, subId, setRequestsData]);

    useEffect(() => {
      syncToRequestsData();
    }, [syncToRequestsData]);

    const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
    const appliesDictionary = useDictionary(Dictionary, DICTIONARY.Applies);
    const refundsDictionary = useDictionary(
      Dictionary,
      DICTIONARY.RefundsMedicalEquipment,
    );

    const refundFallbackAlertText = useMemo(() => {
      if (!locationState?.refundFailureReason) return undefined;
      if (
        locationState.refundFailureReason ===
        REFUND_FALLBACK_REASON.INVOICE_TOO_OLD
      ) {
        return refundsDictionary.fallbackBanner.invoiceTooOld;
      }
      if (
        locationState.refundFailureReason ===
        REFUND_FALLBACK_REASON.MULTIPLE_ITEMS
      ) {
        return refundsDictionary.fallbackBanner.multipleItems;
      }
      return refundsDictionary.fallbackBanner.noItems;
    }, [locationState?.refundFailureReason, refundsDictionary]);

    const prefilledAttachment = useMemo(():
      | IPrefilledAttachment
      | undefined => {
      if (!locationState?.prefilledInvoiceFile) {
        return undefined;
      }

      return { file: locationState.prefilledInvoiceFile };
    }, [locationState?.prefilledInvoiceFile]);

    const [searchParams] = useSearchParams();
    const isDepartmentInitiated =
      searchParams.get('initiatedByDepartment') === 'true';
    const shemeshApplyNumber = searchParams.get('shemeshApplyNumber');

    const buildNoteForApi = useCallback(
      (userNote: string): string => {
        const sanitized = sanitizeNote(userNote);
        if (isDepartmentInitiated && shemeshApplyNumber) {
          const prefix =
            appliesDictionary.universalRequestInitiatedByDepartmentDescription(
              shemeshApplyNumber,
            );
          return `${prefix}${sanitized}`;
        }
        return sanitized;
      },
      [appliesDictionary, isDepartmentInitiated, shemeshApplyNumber],
    );

    useEffect(() => {
      if (isNoteError) {
        const el = document.getElementById('request-note');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [isNoteError]);

    const validateFormSubmitHandler = useCallback((): boolean => {
      const isNoteEmpty = note.length === 0;
      if (isNoteEmpty) {
        setIsNoteError(true);
        return false;
      }

      return true;
    }, [note]);

    const dispatch = useDispatchApp();

    const otpData = useAppSelector(
      ({ otpLoginDetails: { otpLoginDetails } }) => otpLoginDetails,
    );

    const initPersonalDetails = useCallback(async () => {
      setIsPageLoading(true);
      if (!uploadFilesList) getUploadFileList();

      if (!otpData) {
        const result = await getPersonalDetails();
        if (result) dispatch(setPersonalDetails(result));
      }
      setIsPageLoading(false);
    }, [dispatch, getUploadFileList, otpData, uploadFilesList]);

    useEffect(() => {
      window.scroll(0, 0);
      initPersonalDetails();
    }, [initPersonalDetails, imposterId]);

    const handleSubmit = async () => {
      setIsPageLoading(true);

      if (!validateFormSubmitHandler()) {
        setIsPageLoading(false);
        return;
      }

      syncToRequestsData();

      const response = await createRequest(
        dataMapper(
          {
            ...requestsData,
            note: buildNoteForApi(note),
            type: subId,
          },
          subId,
          otpData || personalDetails,
        ),
      );

      setIsPageLoading(false);
      if (response) {
        if (response.data?.internetReqNum) {
          setInternetReqNum(response.data.internetReqNum);
          setIsDialogOpen(true);
          handleSuccess();
        } else if (
          parseInt((response as any)?.response?.status) !==
          HTTP_STATUS_CODES.Forbidden
        ) {
          navigate('/error');
        }
      }
    };

    const onSetNote = (value: string) => {
      if (isNoteError && value.length > 0) {
        setIsNoteError(false);
      }
      setNote(value);
    };

    const navigate = useNavigate();

    const { hasAccess: hasVeteranDisabilityCert } = useDisabilityCertAccess();

    const shouldShowRecommendation = useCallback(() => {
      if (
        !isOtp &&
        subId === DISABLED_VETERAN_SUB_ID &&
        !hasVeteranDisabilityCert
      ) {
        return false;
      }
      return !!onlineFormLink && onlineFormLink !== '';
    }, [onlineFormLink, hasVeteranDisabilityCert, isOtp, subId]);

    useEffect(() => {
      if (subId === IRON_SWORDS_FORM_ID)
        setNote(dictionary.uploadFile.inputFor300);
    }, [dictionary.uploadFile.inputFor300, subId]);

    useEffect(() => {
      const initalNote = searchParams.get('note');
      if (initalNote && !isDepartmentInitiated) {
        onSetNote(initalNote);
      }
    }, [searchParams, isDepartmentInitiated]);

    const noteMaxLength = isDepartmentInitiated
      ? MAX_DEPARTMENT_INITIATED_USER_NOTE_LENGTH
      : undefined;

    const handleUploadComplete = useCallback((documentTypes: any[]) => {
      setUploadedDocumentTypes(documentTypes);
    }, []);

    return (
      <>
        {isPageLoading ? (
          <Loader />
        ) : isDialogOpen ? (
          <SuccessDialog
            title={titleLabel}
            userData={otpData || personalDetails}
            reqNum={internetReqNum}
            resetRequest={resetRequest}
          />
        ) : (
          <>
            {refundFallbackAlertText && (
              <PersonalAreaRecommendation alertText={refundFallbackAlertText} />
            )}
            {shouldShowRecommendation() && (
              <PersonalAreaRecommendation
                linkUrl={onlineFormLink || PERSONAL_RECOMMENDATION_URLS[subId]}
                isDownload={isDownload}
                isDisabledVeteran={
                  subId === DISABLED_VETERAN_SUB_ID &&
                  !isOtp &&
                  hasVeteranDisabilityCert
                }
                subCategory={subCategory}
              />
            )}
            <Box
              sx={
                isDesktop
                  ? {
                      width: '680px',
                      margin: '60px auto',
                    }
                  : { padding: '32px 24px' }
              }
            >
              <NoteTextField
                value={note}
                onChange={onSetNote}
                error={isNoteError}
                maxLength={noteMaxLength}
                title={
                  locationState?.customNoteTitle
                    ? `${locationState.customNoteTitle} ${
                        Dictionary[DICTIONARY.General].required
                      }`
                    : subId === IRON_SWORDS_FORM_ID
                      ? dictionary.uploadFile.noteFor300
                      : undefined
                }
                titleVariant='body1'
                counterColor='#344266'
              />
              <DocumentUploadManager
                availableDocumentTypes={availableDocumentTypes}
                onUploadComplete={handleUploadComplete}
                prefilledAttachment={prefilledAttachment}
              />
              <ActionButtons handleBack={goBack} handleSubmit={handleSubmit} />
            </Box>
          </>
        )}
      </>
    );
  },
);
